import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ClientJS } from 'clientjs';
import { v4 as uuidv4 } from 'uuid';

import { Env, Storages } from 'src/appConstants';
import useUserStore from 'src/hooks/stores/useUserStore';
import { Sections } from 'src/legacy/Components/auth/_constants';
import { useAuthModal } from 'src/modules/auth-modal-store';
import {
	recordSentryApiError,
	recordSentryApiRequest,
	recordSentryApiResponse,
} from 'src/modules/sentry-api-observability';
import { encodeBase64 } from 'src/utils/base64';

// TODO: remove after fully remove legacy code (where retryOnAuth is called when using axios)
type LegacyAxiosConfig = AxiosRequestConfig & {
	retryOnAuth?: boolean;
	skipAuthHeader?: boolean;
	skipAuthModalOn401?: boolean;
};
type LegacyAxiosError = AxiosError & { config: LegacyAxiosConfig };
type AxiosCancellationError = {
	__CANCEL__?: boolean;
	code?: string;
	name?: string;
};

const client = new ClientJS();
let cachedClientFingerprint: number | null = null;
let pendingClientFingerprint: Promise<number> | null = null;

const CLIENT_FINGERPRINT_STORAGE_KEY = '@uticket:client-fingerprint:v1';
const CLIENT_FINGERPRINT_TTL_MS = 1000 * 60 * 60 * 24 * 30;

type StoredClientFingerprint = {
	value: number;
	createdAt: number;
};

const getClientUUID = () => {
	if (!window.localStorage.getItem(Storages.CLIENT_UUID)) {
		window.localStorage.setItem(Storages.CLIENT_UUID, uuidv4());
	}
	return window.localStorage.getItem(Storages.CLIENT_UUID);
};

const readStoredClientFingerprint = () => {
	try {
		const stored = window.localStorage.getItem(CLIENT_FINGERPRINT_STORAGE_KEY);
		if (!stored) return null;

		const parsed = JSON.parse(stored) as StoredClientFingerprint;
		const isValid =
			typeof parsed.value === 'number' &&
			typeof parsed.createdAt === 'number' &&
			Date.now() - parsed.createdAt < CLIENT_FINGERPRINT_TTL_MS;

		return isValid ? parsed.value : null;
	} catch {
		return null;
	}
};

const writeStoredClientFingerprint = (fingerprint: number) => {
	try {
		window.localStorage.setItem(
			CLIENT_FINGERPRINT_STORAGE_KEY,
			JSON.stringify({ value: fingerprint, createdAt: Date.now() })
		);
	} catch {
		// Storage failures should not block API requests.
	}
};

const getCachedClientFingerprint = () => {
	if (cachedClientFingerprint !== null) return cachedClientFingerprint;
	const storedClientFingerprint = readStoredClientFingerprint();
	if (storedClientFingerprint !== null) {
		cachedClientFingerprint = storedClientFingerprint;
		return cachedClientFingerprint;
	}
	return null;
};

const calculateClientFingerprint = () => {
	cachedClientFingerprint = client.getFingerprint();
	writeStoredClientFingerprint(cachedClientFingerprint);
	return cachedClientFingerprint;
};

const ensureClientFingerprint = () => {
	const cachedFingerprint = getCachedClientFingerprint();
	if (cachedFingerprint !== null) return Promise.resolve(cachedFingerprint);

	if (!pendingClientFingerprint) {
		pendingClientFingerprint = Promise.resolve()
			.then(calculateClientFingerprint)
			.finally(() => {
				pendingClientFingerprint = null;
			});
	}
	return pendingClientFingerprint;
};

const scheduleClientFingerprint = () => {
	if (getCachedClientFingerprint() !== null || pendingClientFingerprint) return;

	const run = () => {
		ensureClientFingerprint().catch(() => {
			pendingClientFingerprint = null;
		});
	};
	if ('requestIdleCallback' in window) {
		window.requestIdleCallback(run, { timeout: 3000 });
		return;
	}
	globalThis.setTimeout(run, 1000);
};

const isAxiosCancellation = (error: unknown) => {
	if (axios.isCancel(error)) return true;
	if (!error || typeof error !== 'object') return false;
	const cancellation = error as AxiosCancellationError;
	return (
		cancellation.__CANCEL__ === true ||
		cancellation.code === 'ERR_CANCELED' ||
		cancellation.name === 'CanceledError'
	);
};

const requiresClientFingerprint = (config: AxiosRequestConfig) => {
	const url = config.url ?? '';
	return (
		/^\/?sell\/[^/]+\/(buy|pay|edit|close)/.test(url) ||
		/^\/?sell\/open/.test(url)
	);
};

type SentryAxiosConfig = LegacyAxiosConfig & {
	sentryMeta?: { apiIssueCaptured?: boolean };
};

export const toRejectedApiError = (error: LegacyAxiosError): Error => {
	const response = error.response;

	if (!response) {
		const networkError = new Error(error.message || 'Network Error');
		networkError.name = 'ApiNetworkError';
		return networkError;
	}

	const responseData = response.data as { message?: string } | undefined;
	const message =
		typeof responseData?.message === 'string'
			? responseData.message
			: `HTTP ${response.status}`;

	const apiError = new Error(message);
	apiError.name = 'ApiResponseError';

	Object.assign(apiError, {
		status: response.status,
		statusText: response.statusText,
		data: response.data,
		config: response.config,
		headers: response.headers,
		request: response.request,
	});

	return apiError;
};

const createRejectedApiError = (error: LegacyAxiosError) => {
	const rejected = toRejectedApiError(error);
	const apiIssueCaptured = (error.config as SentryAxiosConfig | undefined)
		?.sentryMeta?.apiIssueCaptured;

	if (apiIssueCaptured) {
		Object.assign(rejected, { apiIssueCaptured: true });
	}

	return rejected;
};

const openAuthModal = (error: LegacyAxiosError) => {
	return new Promise((resolve, reject) => {
		const isUserStatusPending = error.response?.status === 428;
		useAuthModal.setState(prev => ({
			isOpen: true,
			closeOnOverlayClick: false,
			defaultSection: isUserStatusPending
				? Sections.COMPLETE_SINGUP
				: Sections.LOGIN,
			onOverlayClick: () => {
				useUserStore.getState().clear();
				window.location.replace('/');
			},
			resolvers: error.config.retryOnAuth
				? [...prev.resolvers, resolve]
				: prev.resolvers,
			rejecters: error.config.retryOnAuth
				? [...prev.rejecters, reject]
				: prev.rejecters,
		}));
	})
		.then((authResponse: any) => {
			useAuthModal.setState({ isOpen: false, resolvers: [], rejecters: [] });
			const { email, token } = authResponse ?? {};
			if (!email || !token) {
				return Promise.reject(createRejectedApiError(error));
			}

			const credentials = `${email}:${token}`;
			const encodedCredentials = encodeBase64(credentials);
			error.config.headers.Authorization = `Basic ${encodedCredentials}`;
			return axios.request(error.config);
		})
		.catch(err =>
			Promise.reject(
				axios.isAxiosError(err)
					? createRejectedApiError(err as LegacyAxiosError)
					: createRejectedApiError(error)
			)
		);
};

export function requestFullfilledAuthHeader(config: AxiosRequestConfig) {
	if ((config as LegacyAxiosConfig).skipAuthHeader) return config;

	const { id, accessToken, token, email } = useUserStore.getState();
	if (!accessToken) return config;
	if (Env === 'development') {
		config.headers['X-Token'] = token;
		config.headers['X-User-Id'] = id;
		config.headers['X-User-Email'] = email;
		config.headers['X-User-Level'] = 1;
	} else {
		config.headers.Authorization = `Basic ${accessToken}`;
	}
	return config;
}

export async function requestFullfilledCustomHeaders(
	config: AxiosRequestConfig
) {
	const correlationId = getClientUUID() ?? uuidv4();
	const shouldAwaitFingerprint =
		Env !== 'development' && requiresClientFingerprint(config);
	const cachedFingerprint =
		Env === 'development' ? null : getCachedClientFingerprint();
	const fingerprint =
		cachedFingerprint ??
		(shouldAwaitFingerprint ? await ensureClientFingerprint() : null);

	if (Env !== 'development' && fingerprint === null) {
		scheduleClientFingerprint();
	}
	config.headers = {
		...config.headers,
		'X-Correlation-ID': correlationId,
		...(fingerprint !== null ? { 'X-Fingerprint': fingerprint } : {}),
	};

	recordSentryApiRequest(config, correlationId);
	return config;
}

export function responseFullfilled(response: AxiosResponse) {
	recordSentryApiResponse(response);
	return response;
}

export function responseBlob(response: AxiosResponse) {
	return new Blob([response.data], {
		type: response.headers['content-type'],
	});
}

export function responseRejectedNotFound(error: LegacyAxiosError) {
	if (error.response?.status === 401 && !error.config?.skipAuthModalOn401) {
		return openAuthModal(error);
	}
	return error;
}

export function responseRejected(error: LegacyAxiosError) {
	if (isAxiosCancellation(error)) return Promise.reject(error);
	if (error.isAxiosError) {
		recordSentryApiError(error);
		return Promise.reject(createRejectedApiError(error));
	}

	return Promise.resolve(error);
}

export function responseArrayBuffer(error: LegacyAxiosError) {
	if (error.response?.data instanceof ArrayBuffer) {
		const decoder = new TextDecoder('utf-8');
		error.response.data = JSON.parse(decoder.decode(error.response.data));
	}
	return error;
}

type ApiError = {
	code: string;
	message: string;
	status: number;
};

type ApiErrorPayload = {
	status?: number;
	code?: string;
	message?: string;
	data?: { status?: number; code?: string; message?: string };
	response?: {
		status?: number;
		data?: { status?: number; code?: string; message?: string };
	};
};

const UNEXPECTED_API_ERROR: ApiError = {
	code: 'unexpected',
	message: 'Ocorreu um erro inesperado',
	status: 500,
};

export function getApiError(error: unknown): ApiError {
	try {
		if (error == null) {
			return UNEXPECTED_API_ERROR;
		}

		const payload = error as ApiErrorPayload;
		const data = payload.data ?? payload.response?.data;
		const status =
			data?.status ?? payload.status ?? payload.response?.status ?? 500;

		if (data && typeof data === 'object') {
			return {
				code: data.code ?? payload.code ?? 'unexpected',
				message:
					data.message ?? payload.message ?? UNEXPECTED_API_ERROR.message,
				status,
			};
		}

		if (payload.code || payload.message) {
			return {
				code: payload.code ?? 'unexpected',
				message: payload.message ?? UNEXPECTED_API_ERROR.message,
				status,
			};
		}

		return {
			...UNEXPECTED_API_ERROR,
			status,
		};
	} catch {
		return UNEXPECTED_API_ERROR;
	}
}

export function getRejectedApiMessage(
	error: unknown,
	fallback?: string
): string | undefined {
	if (error == null) {
		return fallback;
	}

	const payload = error as ApiErrorPayload;
	const data = payload.data ?? payload.response?.data;

	if (data && typeof data === 'object' && typeof data.message === 'string') {
		return data.message;
	}

	return fallback;
}
